import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, Navigate } from "react-router-dom";
import { useFormikContext } from 'formik';
import { Box, Card, Grid, Spinner, Text } from "theme-ui";
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import {
  Heading,
  TextInput,
  NumberInput,
  Form,
  Button,
  SingleSelect,
  DataList,
  Card as LumosCard,
  AlertInline
} from "@loanpal/lumos__core";
import { object, string } from "yup";
import glpPlusLogoWhite from "../../goodleapplus/images/GLplus_white_gradient.svg";
import Loading from "../../../utils/loading";
import {
  phone,
  phoneDigits,
  numberToMoney,
  numberToMoneyNoDecimal,
  moneyStringToNumber,
  validateMoneyInput
} from "../../../utils/format";
import { default as ErrorPage } from "../../../utils/error";
import { default as NotFoundPage } from "../../../utils/not-found";
import { isGoodLeapPlusDomain } from "../../../utils/is-goodleapplus-domain";
import { notifyAdminErrorMessage } from "../../../utils/api";
import { getToken as getTokenApi,
         getPayment as getPaymentApi,
         getCategories as getCategoriesApi,
         getLimitations as getLimitationsApi,
         getOffersForSharedApp,
         parseSharedAppOfferIds,
         filterOffersByConfiguredIds,
         getOfferRowId,
         getUserByEmail,
         postLoanShare as postLoanShareApi } from "../../../utils/api";

import ReCAPTCHA from "react-google-recaptcha";
import { Loader } from '@aws-amplify/ui-react';

// api
import { API, graphqlOperation } from "aws-amplify";
import { getSharedApp } from "../../../graphql/queries";
import { createGoodLinks } from "../../../graphql/mutations";

import HOME_IMPROVEMENT from "../../../images/HI_1.png";
import ROOF from "../../../images/ROOF_1.png";
import SOLAR from "../../../images/SOLAR_1.png";
import BATTERY from "../../../images/BATTERY_1.png";
import REMODELING from "../../../images/REMODEL_1.png";

/** GoodLeap Plus apply flow — maroon/gold accents on forms; banner uses purple gradient vs teal banner (#003439) */
const GLP_BRAND = {
  maroon: "#762123",
  maroonHover: "#5c1a1c",
  gold: "#f2b533",
  black: "#000000",
  white: "#ffffff",
};

const glpBannerLogoStyle = {
  width: 230,
  maxWidth: "min(90%, 280px)",
  margin: "0 auto",
  display: "block",
  padding: "12px 16px",
};

// import { install, gtag } from '@analytics/google-analytics';

const Home = () => {
  const [token, setToken] = useState("");
  const [spinner, setSpinner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingPayment, setLoadingPayment] = useState(true);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [error, setError] = useState({error: false, notifySlack: false});
  const [errorMessage, setErrorMessage] = useState("");
  const [notFound, setNotFound] = useState({ error: false, status: ""});
  const { id, client, offerId } = useParams();
  const [SharedApp, setSharedApp] = useState();
  const [payment, setPayment] = useState();
  const [displayResult, setDisplayResult] = useState(true);
  const [states, setStates] = useState();
  const [statesItems, setStatesItems] = useState([]);
  const [goodLink, setGoodLink] = useState();
  const [canApply, setCanApply] = useState(false);
  const [minAmount, setMinAmount] = useState();
  const [maxAmount, setMaxAmount] = useState();
  const [limitations, setLimitations] = useState();
  const [offers, setOffers] = useState();
  const [offerItems, setOfferItems] = useState([])
  const [singleOffer, setSingleOffer] = useState("");
  const [paymentAmount, setPaymentAmount] = useState({});
  const [singleState, setSingleState] = useState("");
  const [offerDesc, setOfferDesc] = useState();
  const [offerName, setOfferName] = useState();
  const phoneRegExp = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
  const captchaRef = useRef(null);
  const [timer, setTimer] = useState(null);
  const [elevatedToken, setElevatedToken] = useState(null);
  const [userData, setUserData] = useState({firstName: "", lastName: "", email: "", phone: ""});
  const [salesRepUnauthorizedWarning, setSalesRepUnauthorizedWarning] = useState(false);
  /** Ignore stale getOffers responses when SharedApp/token/effect re-fires. */
  const getOffersFetchIdRef = useRef(0);

const version = "2.0";

/* Query String parameters
*  values: ?cost=18000&lname=Sanchez&fname=Hector&phone=650-207-7716&email=hsanchez@goodleap.com&loanterm=42a10ba5-a352-429a-ba3d-c84cb44cd9e3&state=AZ&locked=true&salesRepEmail=hsanchez@goodleap.com
*
*  salesRepEmail: the email of the sales rep to be used for the application.  If not provided, the default user will be used.
*/

  // Prepopulate with query string paramenters
  const [ searchParams ] = useSearchParams();
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    phone: '',
    offerId: '',
    locked: false,
    productType: '',
    state: '',
    refnum: '',
    cost: '',
    channel: ''
  });

  useEffect(() => {
    const id = "goodleapplus-google-font-lato";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,300;0,400;0,700;0,900;1,400&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const salesRepEmail = searchParams.get("salesRepEmail");
    if (!SharedApp || !salesRepEmail) return;

    const needsElevatedToken =
      SharedApp.useConnectedOrgs &&
      SharedApp.parentOrganizationClientId &&
      SharedApp.organizationId;

    if (needsElevatedToken && !elevatedToken) return;

    getUserData(
      salesRepEmail,
      needsElevatedToken ? elevatedToken.token : undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SharedApp, elevatedToken, searchParams]);

  const salesRepFromUserRecord = (user) => ({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phone: user?.phoneNumber?.number || user?.phones?.[0]?.number || "",
  });

  const getUserData = async (email, token) => {
    console.log("Getting user data for", email);
    setSalesRepUnauthorizedWarning(false);
    try {
      const response = await getUserByEmail(SharedApp.clientId, email, token);
      console.log("userData response", response);
      if (!response || !Array.isArray(response.data)) {
        return;
      }
      if (response.data.length === 0) {
        setSalesRepUnauthorizedWarning(true);
        return;
      }
      setUserData(salesRepFromUserRecord(response.data[0]));
      console.log("userData", userData);
    } catch (err) {
      console.log("error getting user data", err);
    }
  }

  useEffect(() => {
    setFormData({
      fname: searchParams.get('fname') || '',
      lname: searchParams.get('lname') || '',
      email: searchParams.get('email') || '',
      phone: searchParams.get('phone') || '',
      locked: searchParams.get('locked') === "true" || searchParams.get('locked') === "false" ? JSON.parse(searchParams.get('locked')) : false,
      prodType: searchParams.get('producttype') || '',
      state: searchParams.get('state') || '',
      refnum: searchParams.get('refnum') || '',
      offerId: searchParams.get('loanterm') || '',
      cost: searchParams.get('cost') || '',
      channel: searchParams.get('channel') || ''
    });
  }, [searchParams]);

  useEffect(() => {
    fetchSharedApp(id);
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
      if (paymentAmount.payment) {
        getPayment(singleOffer, paymentAmount.amount);
      }
    // eslint-disable-next-line
  }, [paymentAmount])

  const getElevatedToken = async (clientId, parentOrganizationClientId, organizationId) => {
    const elevatedToken = await getTokenApi(parentOrganizationClientId, organizationId);
    if (elevatedToken) {
      console.log("Elevated token received", elevatedToken);
      setElevatedToken(elevatedToken);
    }
  }

  useEffect(() => {
    if (SharedApp && SharedApp.useConnectedOrgs && SharedApp.parentOrganizationClientId && SharedApp.organizationId) {
      console.log("Getting elevated token");
      getElevatedToken(SharedApp.clientId, SharedApp.parentOrganizationClientId, SharedApp.organizationId);
    }
  }, [SharedApp]);

  /** Drop prior org token so getOffers does not run with a stale token for a new SharedApp. */
  useEffect(() => {
    getOffersFetchIdRef.current += 1;
    setElevatedToken(null);
  }, [SharedApp?.id]);

  useEffect(() => {
    if (SharedApp && SharedApp.useConnectedOrgs && SharedApp.parentOrganizationClientId && SharedApp.organizationId && elevatedToken) {
      console.log("with token");
      setPaymentAmount({ amount: SharedApp.loanAmount, payment: false });
      getOffers(SharedApp.clientId, SharedApp.channel, SharedApp.loanAmount);
      getCategories(SharedApp.clientId, SharedApp.category);
    } else if (SharedApp && !SharedApp.useConnectedOrgs) {
      console.log("without token");
      setPaymentAmount({ amount: SharedApp.loanAmount, payment: false });
      getOffers(SharedApp.clientId, SharedApp.channel, SharedApp.loanAmount);
      getCategories(SharedApp.clientId, SharedApp.category);
    }
    // eslint-disable-next-line
  }, [SharedApp, elevatedToken]);

  useEffect( () => {
    try {
      if (goodLink){
        console.log("This is a goodLink", goodLink);
        API.graphql(
          graphqlOperation(createGoodLinks, { input: goodLink })
        );
      }
    } catch (err) {
      console.log("error fetching Shared Apps");
    }
  }, [goodLink]);

  useEffect(() => {
    if (error.error && errorMessage !== "") {
      notifyAdminErrorMessage(SharedApp, id, errorMessage, error.notifySlack);
    }
    // eslint-disable-next-line
  }, [error, errorMessage]);

const productItems = [
    { label: 'Loan product', text: offerDesc},
    {
      label: 'Term and rate',
      text: offerName,
    }
  ];

const estimatedPayment = payment ? payment[0].calculations.map((pay) => ({
    label: pay.paymentDescription,
    text: <div>
          {loadingPayment ? (
            <SkeletonTheme baseColor="#d7d7d7" highlightColor="#f5f5f5">
              <Text variant="h3" as="h2">
                $ <Skeleton width={125} height={24} />
              </Text>
            </SkeletonTheme>
          ) : (
            <Text variant="h3" as="h2">
              {pay.amount === null ? `${numberToMoney(pay.minimumAmount.value)} - ${numberToMoney(pay.maximumAmount.value)}` : numberToMoney(pay.amount.value)}
              <span style={styles.period} color="neutral.base">
              &nbsp;{pay.interval === "MONTHLY" ? "/ month" : ""}
              </span>
            </Text>
          )}
          </div>,
  })) : {};

    const getPayment = async (offerId, loanAmount) => {
    setLoadingPayment(true);
    if (offerId === "" || loanAmount === "" || loanAmount <= minAmount || loanAmount > maxAmount || !validateMoneyInput(loanAmount)) {
      //TODO Throw errors if loanAmount is pass max or under min
      return;
    }
    try {
      const clientId = SharedApp.clientId;
      await getPaymentApi(clientId, offerId, loanAmount, ["AUTOPAY"], elevatedToken ? elevatedToken.token : undefined)
      .then((response) => {
        setPayment(response);
        setLoadingPayment(false);
      },
      (err) => {
        setErrorMessage("Error getting payments " + err);
        setError({error:true, notifySlack: false});
        console.log("Error getting payments ", err);
        setLoadingPayment(false);
      })

    } catch (err) {
      console.log("error getting payments");
      setLoadingPayment(false);
      return false;
    }
  };

  const getCategories = async (clientId, category) => {
    try {
      await getCategoriesApi(clientId, elevatedToken ? elevatedToken.token : undefined)
      .then((response) => {
        const cat = response.find(el => el.name === getCategoryName(category).toUpperCase());
        getLimitations(clientId, cat.id);
        setMinAmount(cat.minLoanAmount);
        setMaxAmount(cat.maxLoanAmount);
      },
      (err) => {
        setErrorMessage("Error getting categories " + err);
        setError({error:true, notifySlack: false});
        console.log("Error getting categories", err);
        setLoadingPayment(false);
      })
    } catch (err) {
      console.log("error getting categories", err);
      return false;
    }
  }
  const getLimitations = async (clientId, category) => {
    try {
      await getLimitationsApi(clientId, elevatedToken ? elevatedToken.token : undefined)
      .then((response) => {
        const limitations = response.data.filter((element) => element.categoryId === category)
        setLimitations(limitations);
      },
      (err) => {
        setErrorMessage("Error getting limitations " + err);
        setError({error:true, notifySlack: false});
        console.log("Error getting limitations", err);
        setLoadingPayment(false);
      })
    } catch (err) {
      console.log("error getting limitations", err);
      return false;
    }
  }

    const getOffers = async (clientId, channel, loanAmount, state) => {
    const fetchId = ++getOffersFetchIdRef.current;
    const isStale = () => fetchId !== getOffersFetchIdRef.current;
    try {
      const sharedAppOfferParams = {
        useConnectedOrgs: SharedApp.useConnectedOrgs,
        parentOrganizationClientId: SharedApp.parentOrganizationClientId,
        organizationId: SharedApp.organizationId,
        token: elevatedToken ? elevatedToken.token : null
      };

      const ch = typeof channel === "string" ? channel.trim() : "";
      let catalogRows = [];

      if (ch) {
        const chRes = await getOffersForSharedApp({
          clientId,
          channel: ch,
          ...sharedAppOfferParams,
        });
        if (isStale()) return false;
        if (chRes && Array.isArray(chRes.data)) {
          catalogRows = chRes.data;
        }
      }

      const configuredIds = parseSharedAppOfferIds(SharedApp.offers);

      if (catalogRows.length === 0 && configuredIds.length > 0) {
        const allRes = await getOffersForSharedApp({
          clientId,
          allOffers: true,
          ...sharedAppOfferParams,
        });
        if (isStale()) return false;
        const pool = allRes && Array.isArray(allRes.data) ? allRes.data : [];
        catalogRows = filterOffersByConfiguredIds(pool, configuredIds);
      }

      let data = catalogRows;

      if (data.length === 0) {
        if (isStale()) return false;
        setOffers([]);
        setOfferItems([]);
        setErrorMessage(
          configuredIds.length > 0
            ? "Error getting offers: no offers for this channel, and configured offer ids did not match any promotion offer."
            : "Error getting offers: no offers for this channel, and no configured offers to fall back to."
        );
        setError({ error: true, notifySlack: true });
        console.log("Error getting offers, no offers returned");
        return false;
      }

      if (offerId !== undefined) {
        const so = data.find((o) => getOfferRowId(o) === offerId);
        data = so ? [so] : [];
      }
      if (data.length > 0) {
        if (isStale()) return false;
        setError({ error: false, notifySlack: false });
        setErrorMessage("");
        setOffers(data);
        let offersArrayOptions = [];
        offersArrayOptions = data
          .map((element) => [
            ...offersArrayOptions,
            { name: element.productCode.includes("DEFERRED") ? `${element.productName} - ${element.name}` : `${element.name} - ${element.productName}`, value: getOfferRowId(element) },
          ])
          .flat();
        setOfferItems(offersArrayOptions);
        if (data.length === 1) {
          setSingleOffer(getOfferRowId(data[0]));
          setOfferDesc(data[0].productDescription);
          setOfferName(data[0].name);
          let stateArrayOptions = [];
          if (!!JSON.parse(SharedApp.states)) {
            const sts = JSON.parse(SharedApp.states).split(',');
            setStates(JSON.stringify(SharedApp.states));
            if (sts.length === 1) {
              setSingleState(sts[0]);
            }
            stateArrayOptions = sts.map((element) => [
              ...stateArrayOptions,
              { name: element, value: element },
            ])
            .flat();
          } else {
            setStates(JSON.stringify(data[0].states));
            if (data[0].states.length === 1) {
              setSingleState(data[0].states[0]);
            }
            stateArrayOptions = data[0].states
              .map((element) => [
                ...stateArrayOptions,
                { name: element, value: element },
              ])
              .flat();
          }
          setStatesItems(stateArrayOptions);
          getPayment(getOfferRowId(data[0]), loanAmount);
        } else if (searchParams.get('loanterm') && data.length > 1) {
          const offerParam = data.find((o) => getOfferRowId(o) === searchParams.get('loanterm'));
          if (offerParam) {
            setSingleOffer(getOfferRowId(offerParam));
            setOfferDesc(offerParam.productDescription);
            setOfferName(offerParam.name);
            setStates(JSON.stringify(offerParam.states));
            if (state) {
              setSingleState(state);
            } else if (offerParam.states.length === 1) {
              setSingleState(offerParam.states[0]);
            } else if (offerParam.states.length > 1 && offerParam.states.find((st) => st === formData.state) !== undefined) {
              setSingleState(formData.state);
            }
            let stateArrayOptions = [];
            stateArrayOptions = offerParam.states
              .map((element) => [
                ...stateArrayOptions,
                { name: element, value: element },
              ])
              .flat();
            setStatesItems(stateArrayOptions);
            getPayment(getOfferRowId(offerParam), loanAmount);
          }
        }
      } else {
        if (isStale()) return false;
        setErrorMessage("Error getting offers, no offers returned");
        setError({ error: true, notifySlack: true });
        console.log("Error getting offers, no offers returned after loanterm filter");
      }
    } catch (err) {
      if (isStale()) return false;
      setErrorMessage("Error getting offers " + err);
      setError({ error: true, notifySlack: true });
      console.log("error getting Offers", err);
      return false;
    }
  };

  async function fetchSharedApp(id) {
    try {
      const appData = await API.graphql(
        graphqlOperation(getSharedApp, { id: id })
      );
      const { clientId: cId, status: st } = appData.data.getSharedApp;
      const displayStatuses = ["ACTIVE", "TEST"];
      const clientInPath = client != null && String(client).trim() !== "";
      const clientMismatch =
        clientInPath &&
        cId.toLowerCase() !== String(client).trim().toLowerCase();
      if (clientMismatch || displayStatuses.indexOf(st) < 0) {
        setLoading(false);
        setNotFound({ error: true, status: st });
        return null;
      }
      if (searchParams.get('cost') && searchParams.get('cost') !== "") {
        appData.data.getSharedApp.loanAmount = searchParams.get('cost');
      }

      setSharedApp(appData.data.getSharedApp);
      // analytics tracking
      // install("G-KBMPQSEG5M", {
      //   page_title: `GoodLeap GoodLink ${appData.data.getSharedApp.clientId}`,
      //   page_location: `https://${window.location.host}/apply/${id}`,
      // });
      // gtag("set", "clientId", appData.data.getSharedApp.clientId);
      // gtag("event", "page_view");
      setDisplayResult(JSON.parse(appData.data.getSharedApp.displayIframe));
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setNotFound({ error: true, status: "ERROR" });
      console.log("error fetching SharedApp", err);
    }
  }

  const handleClick = async (payload) => {
    try {
      setSpinner(true);
      const data = await postLoanShareApi(SharedApp.clientId, payload, elevatedToken ? elevatedToken.token : undefined);
      setIframeLoading(true);
      setTimeout(() => setIframeLoading(false), 5000);
      setToken(data.link);
      if (data.link !== "") setSpinner(false);
    } catch (err) {
      setSpinner(false);
      setErrorMessage("Error creating Shared App " + payload);
      setError({error:true, notifySlack: true});
      console.log("error creating Shared App", err);
    }
  };

  const handleCaptchaChange = async(value) => {
    setSpinner(true);
    const myValues = {
        queryStringParameters: {
          response: value
       },
      };
      const response = await API.post("developerApi", "/siteverify", myValues);
      if (response.success) {
        setCanApply(true);
      } else {
        setCanApply(false);
      }
      setSpinner(false);
  }

  const buildGoodLink = (values) => {
    if (values.email.includes("@goodleap.com")) return null;
    const gLink = {
      clientId: SharedApp.clientId,
      custFirstName: values.firstName,
      custLastName: values.lastName,
      custEmail: values.email,
      custMobile: values.mobile,
      custState: values.state,
      custLoanAmount: values.loanAmount,
      emailSent: SharedApp.sendEmail,
    };
    console.log(gLink);
    setGoodLink(gLink);
  }

  const getReferenceId = (refId, mobile, lastName) => {
    console.log(refId);
    return refId === "lastName" ? lastName
           : refId === "mobilePhone" ? phoneDigits(mobile)
           : refId === "quickLink" ? "QuickLink"
           : refId !== "" ? SharedApp.referenceId
           : refId === "goodleapplus" ? "GoodLeapPlus"
           : ""
  }

  const buildPaydown = (values) => {
    if (
      !values.firstName ||
      !values.lastName ||
      !values.email ||
      !values.state ||
      !values.mobile ||
      !values.offer
    ) {
      return;
    }
    buildGoodLink(values);
    const {
      referenceId,
      offer,
      channel,
      state,
      email,
      firstName,
      lastName,
      mobile,
      salesRepEmail,
      salesRepFirstName,
      salesRepLastName,
      salesRepPhone,
      loanAmount,
      autopayIntent,
      sendEmail,
    } = values;

    return JSON.stringify({
      referenceNumber: getReferenceId(referenceId, mobile, lastName),
      offerId: offer,
      channel: channel,
      state: state,
      applicant: {
        email: email,
        firstName: firstName,
        lastName: lastName,
      },
      submittingUser: {
        firstName: salesRepFirstName,
        lastName: salesRepLastName,
        email: salesRepEmail.trim(),
        phone: salesRepPhone,
      },
      amount: {
        value: loanAmount,
        type: "USD",
      },
      sendEmail: sendEmail || false,
      enrollments: [autopayIntent ? "AUTOPAY" : ""],
    });
  };

  const getCategory = (category) => {
    if (category === "HOME_IMPROVEMENT") return HOME_IMPROVEMENT
    else if (category === "ROOF") return ROOF;
    else if (category === "SOLAR") return SOLAR;
    else if (category === "BATTERY") return BATTERY;
    else if (category === "REMODELING") return REMODELING;
    else return "";
  }

  const getCategoryName = (category) => {
    if (category === "HOME_IMPROVEMENT") return "Home Improvement";
    else if (category === "ROOF") return "Roofing / Windows / Doors / Siding / Geothermal / Restoration";
    else if (category === "SOLAR") return "Solar";
    else if (category === "BATTERY") return "Battery";
    else if (category === "REMODELING") return "Remodeling";
    else return "";
  }

  const StatesInput = () => {
    const { setFieldError, setErrors, values } = useFormikContext();
    return (
      <>
      <SingleSelect
        name="state"
        label={"State"}
        size={20}
        items={statesItems}
        readOnly={statesItems.find(st => st.value === formData.state) !== undefined && formData.locked ? true : false}
        dangerouslyOnChange={(target) => {
          if(values.loanAmount !== "" && statesItems.length > 1) {
            setLoadingPayment(true);
            setErrors({});
            const amt = values.loanAmount;
            if (limitations.find(el => el.state === target)) {
              const limits = limitations.find(el => el.state === target);
              if (moneyStringToNumber(amt) < limits.minLoanAmount) {
                setFieldError("loanAmount", `Loan amount needs to be greater than ${numberToMoneyNoDecimal(limits.minLoanAmount)}`);
                setCanApply(false);
              } else if (moneyStringToNumber(amt) > limits.maxLoanAmount) {
                setFieldError("loanAmount", `Loan amount needs to be less than ${numberToMoneyNoDecimal(limits.maxLoanAmount)}`);
                setCanApply(false);
              } else {
                setLoadingPayment(false);
                setCanApply(true);
              }
              } else {
              if (moneyStringToNumber(amt) < minAmount) {
                setFieldError("loanAmount", `Loan amount needs to be greater than ${numberToMoneyNoDecimal(minAmount)}`);
                setCanApply(false);
              } else if (moneyStringToNumber(amt) > maxAmount) {
                setFieldError("loanAmount", `Loan amount needs to be less than ${numberToMoneyNoDecimal(maxAmount)}`);
                setCanApply(false);
              } else {
                setLoadingPayment(false);
                setCanApply(true);
              }
            }
          }
        }}
      />
      </>
    )
  }

  const AmountInput = () => {
    const { setFieldValue, setFieldError, setErrors, values } = useFormikContext();
    return (
      <>
      <NumberInput
          key="loanAmount"
          label="Amount to finance"
          name="loanAmount"
          autoComplete="off"
          readOnly={formData.cost !== "" && formData.locked ? true : false}
          onBlurCapture={({ target }) => {
            const moneyValue = moneyStringToNumber(target.value);
            // setPaymentAmount({ amount: moneyStringToNumber(target.value), payment: true });
            if (Number.isNaN(moneyValue)) return;
            setFieldValue('loanAmount', numberToMoney(moneyValue));
          }}
          onChangeCapture={({ target }) => {
            setLoadingPayment(true);
            const moneyValue = moneyStringToNumber(target.value);
            const amt = target.value;
            setPaymentAmount({ amount: moneyStringToNumber(target.value), payment: false });
            setFieldValue('loanAmount', numberToMoney(moneyValue));
            clearTimeout(timer);
            const newTimer = setTimeout(() => {
              if (singleOffer) {
                setErrors({});
                 if (limitations.find(el => el.state === values.state)) {
                   const limits = limitations.find(el => el.state === values.state);
                   if (moneyStringToNumber(amt) < limits.minLoanAmount) {
                    setFieldError("loanAmount", `Loan amount needs to be greater than ${numberToMoneyNoDecimal(limits.minLoanAmount)}`);
                    setCanApply(false);
                  } else if (moneyStringToNumber(amt) > limits.maxLoanAmount) {
                    setFieldError("loanAmount", `Loan amount needs to be less than ${numberToMoneyNoDecimal(limits.maxLoanAmount)}`);
                    setCanApply(false);
                  } else {
                    setPaymentAmount({ amount: moneyStringToNumber(amt), payment: true });
                    setCanApply(true);
                  }
                 } else {
                  if (moneyStringToNumber(amt) < minAmount) {
                    setFieldError("loanAmount", `Loan amount needs to be greater than ${numberToMoneyNoDecimal(minAmount)}`);
                    setCanApply(false);
                  } else if (moneyStringToNumber(amt) > maxAmount) {
                    setFieldError("loanAmount", `Loan amount needs to be less than ${numberToMoneyNoDecimal(maxAmount)}`);
                    setCanApply(false);
                  } else {
                    setPaymentAmount({ amount: moneyStringToNumber(amt), payment: true });
                    setCanApply(true);
                  }
                }
              }
            }, 2000)
            setTimer(newTimer)
          }}
        />
      </>
    )
}

  const glpPageShellSx = {
    fontFamily: "'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    color: GLP_BRAND.black,
    backgroundColor: GLP_BRAND.white,
    minHeight: "100vh",
  };

  const goodLeapPlusBannerNode = (
    <Box
      sx={{
        boxSizing: "border-box",
        width: "100%",
        maxWidth: "960px",
        mt: 0,
        mb: 0,
        mx: "auto",
        p: "24px",
        lineHeight: 0,
        background:
          "linear-gradient(135deg, #200F51 0%, #0D0830 60%, #3A0D5C 100%)",
        borderBottom: `4px solid ${GLP_BRAND.gold}`,
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Heading
        className="heading-box"
        sx={{
          width: "100%",
          maxWidth: "960px",
          margin: "0 auto",
          background: "transparent",
          padding: 0,
        }}
      >
        <img src={glpPlusLogoWhite} alt="GoodLeap Plus" style={glpBannerLogoStyle} />
      </Heading>
    </Box>
  );

  const goodLeapPlusHeaderNode = (
    <>
      {goodLeapPlusBannerNode}
      {salesRepUnauthorizedWarning && (
        <Box sx={{ maxWidth: "960px", mx: "auto", pt: 2, pb: 3 }}>
          <AlertInline
            variant="warning"
            sxProps={{
              borderRadius: "8px",
              overflow: "hidden",
              border: "none",
              borderWidth: 0,
              lineHeight: 1.8
            }}
          >
            The sales rep email provided is not an authorized user in GoodLeap.
            <br />
            Please reach out to the Admin on your account for more details.
            <br />
            You can continue to submit the application using the default user <strong>{SharedApp.submittingUser.firstName} {SharedApp.submittingUser.lastName}</strong> with email <strong>{SharedApp.submittingUser.email}</strong> for the application.<br />            
          </AlertInline>
        </Box>
      )}
    </>
  );

  return (
    <Box sx={glpPageShellSx}>
      {error.error && <ErrorPage sharedApp={SharedApp} errorMessage={errorMessage} />}
      {notFound.error &&
        (isGoodLeapPlusDomain() ? (
          <Navigate to="/" replace />
        ) : (
          <NotFoundPage status={notFound.status} />
        ))}
      {loading && <Loading />}
      {token !== "" && displayResult && !loading && !error.error && !notFound.error && (
        <div className="container">
          {iframeLoading && <Loading />}
          <iframe
            title="GoodLeap Plus financing"
            className="iframe"
            allowFullScreen
            src={token}
          />
        </div>
      )}
      {token !== "" && !displayResult && !loading && !error.error && !notFound.error && (
        <Box
          sx={{
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          {goodLeapPlusHeaderNode}
          <Box sx={{ pl: 6, pr: 6, pt: 4, width: ["960px", "640px", "480px"] }}>
            <>
              <Heading variant="h2">
                Thank you for your interest in GoodLeap financing.
              </Heading>
              <Box sx={{ pt: 2, fontSize: "18px" }}>
                An application form has been sent to your email address.
                <br />
                <br />
                If you have questions, reach out to{" "}
                <Text sx={{ fontWeight: 800 }}>
                  {userData && userData.firstName ? userData.firstName : SharedApp.submittingUser.firstName}{" "}
                  {userData && userData.lastName ? userData.lastName : SharedApp.submittingUser.lastName}
                </Text>{" "}
                at{" "}
                <Text sx={{ fontWeight: 800 }}>
                  {phone(userData && userData.phone ? userData.phone : SharedApp.submittingUser.phone)}
                </Text>{" "}
                or{" "}
                <Text sx={{ fontWeight: 800 }}>
                  {userData && userData.email ? userData.email : SharedApp.submittingUser.email}
                </Text>
                .
              </Box>
            </>
          </Box>
        </Box>
      )}
      {token === "" && !loading && !error.error && !notFound.error && (
        <Box
          sx={{
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          {goodLeapPlusHeaderNode}
          {!offers && (
            <Form>
            <Card
              sx={{
                width: 960,
                margin: "0 auto",
              }}
              className="card-form"
            >
              <Box sx={{ pl: 2, pr: 2, pb: 2 }}>
                <Heading variant="h5">
                  Credit application: complete this form to see an ESTIMATED payment scenario. Use the dollar amount suggested by your company representative.
                </Heading>
                <Box as="p" sx={{ mb: 2 }}>
                  &nbsp;
                </Box>
                <Box sx={{ width: "100%", mb: 2, mt: 2  }}>
                  <Loader
                      variation="linear"
                      emptyColor="#ffffff"
                      filledColor={GLP_BRAND.gold}
                      className="loader"
                      />
                </Box>
                <Box>
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <TextInput
                      name="firstName"
                      label="First name"
                      autoComplete="off"
                      readOnly
                    />
                    <TextInput
                      name="lastName"
                      label="Last name"
                      autoComplete="off"
                      readOnly
                    />
                  </Grid>
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <TextInput
                      name="email"
                      label="Email address"
                      autoComplete="off"
                      type="email"
                      readOnly
                    />
                    <TextInput
                      name="mobile"
                      label="Mobile number"
                      autoComplete="off"
                      type="phone"
                      description="Please enter 10 digit mobile number"
                      readOnly
                    />
                  </Grid>
                   <Grid columns={[1, 2]} gap={[0, 3]}>
                      <>
                       <Box sx={{ width: "100%", mr: 5, mt: 2 }}>
                        <SingleSelect
                          name="offer"
                          label={"Getting offers, one moment..."}
                          readOnly
                          items={[]}
                        />
                      </Box>
                      <Box sx={{ width: "30%", mr: 5 }}>
                      <SingleSelect
                        name="state"
                        label={"State"}
                        readOnly
                        size={20}
                        items={[]}
                      />
                      </Box>
                    </>
                  </Grid>
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <Box sx={{ mt: 2 }}>
                      <TextInput
                        name="loanAmount"
                        label="Amount to finance"
                        autoComplete="off"
                        type="loanAmount"
                        readOnly
                      />
                     </Box>
                  </Grid>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    my: 3,
                  }}
                >
                <Button type="submit" sx={{ mt: 3, px: 5 }} disabled>
                  APPLY NOW
                </Button>
                </Box>
              </Box>
            </Card>
            </Form>
          )}
          {offers && (
          <Form
            initialValues={{
              firstName: formData.fname || "",
              lastName: formData.lname || "",
              email: formData.email || "",
              mobile: formData.phone || "",
              state: singleState,
              offer: singleOffer || "",
              channel: formData.channel ? formData.channel : SharedApp ? SharedApp.channel : "",
              referenceId: formData.referenceNumber ? formData.referenceNumber : SharedApp ? SharedApp.referenceId : "",
              loanAmount: formData.cost ? numberToMoney(formData.cost) : SharedApp ? numberToMoney(SharedApp.loanAmount) : numberToMoney(0.00),
              autopayIntent: true,
              sendEmail: SharedApp ? SharedApp.sendEmail : false,
              salesRepFirstName: userData && userData.firstName ? userData.firstName : SharedApp
                ? SharedApp.submittingUser.firstName
                : "",
              salesRepLastName: userData && userData.lastName ? userData.lastName : SharedApp
                ? SharedApp.submittingUser.lastName
                : "",
              salesRepEmail: userData && userData.email ? userData.email : SharedApp ? SharedApp.submittingUser.email : "",
              salesRepPhone:
                userData && userData.phone ? userData.phone : SharedApp && SharedApp.submittingUser.phone
                  ? SharedApp.submittingUser.phone
                  : "",
            }}
            enableReinitialize={false}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={object({
              firstName: string().required(`Enter your first name`),
              lastName: string().required(`Enter your last name`),
              email: string()
                .email("Enter a valid email address, like j.doe@example.com")
                .required(`Enter the borrower's email address`),
              state: string().required(`Select state`),
              offer: string().required('Select an Offer'),
              mobile: string()
                .matches(phoneRegExp, "Enter a valid phone number")
                .required(`Enter a phone number`),
              loanAmount: string()
                        .required("Required")
                        .test("currenty", `Please enter a currency value`, val => validateMoneyInput(val))
                        .test("minAmount", `Amount must be at least ${numberToMoneyNoDecimal(minAmount)}`, val => moneyStringToNumber(val) > minAmount)
                        .test("maxAmount", `Amount must be less than ${numberToMoneyNoDecimal(maxAmount)}`, val => moneyStringToNumber(val) <= maxAmount)
            })}
            onSubmit={({ ...values }) => {
              console.log("onSubmit", values);
              values.loanAmount = moneyStringToNumber(values.loanAmount);
              // If no error then submit
              handleClick(buildPaydown(values));
            }}
            showSummary={false}
          >
            <Card
              sx={{
                width: 960,
                margin: "0 auto",
              }}
              className="card-form"
            >
              <Box sx={{ pl: 2, pr: 2, pb: 2 }}>
                <Heading variant="h5">
                  Complete this form to see an ESTIMATED payment scenario. Use the dollar amount suggested by your company representative.
                </Heading>
                <Box as="p" sx={{ mb: 2 }}>
                  &nbsp;
                </Box>
                <Box>
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <TextInput
                      name="firstName"
                      label="First name"
                      autoComplete="off"
                      readOnly={formData.fname !== "" && formData.locked ? true : false}
                    />
                    <TextInput
                      name="lastName"
                      label="Last name"
                      autoComplete="off"
                      readOnly={formData.lname !== "" && formData.locked ? true : false}
                    />
                  </Grid>
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <TextInput
                      name="email"
                      label="Email address"
                      autoComplete="off"
                      type="email"
                      readOnly={formData.email !== "" && formData.locked ? true : false}
                    />
                    <TextInput
                      name="mobile"
                      label="Mobile number"
                      autoComplete="off"
                      type="phone"
                      description="Please enter 10 digit mobile number"
                      readOnly={formData.phone !== "" && formData.locked ? true : false}
                    />
                  </Grid>
                  {!offers && (
                  <Box sx={{ width: "100%", mb: 2, mt: 2  }}>
                  <Loader
                      variation="linear"
                      emptyColor="#ffffff"
                      filledColor={GLP_BRAND.gold}
                      className="loader"
                      />
                   </Box>
                   )}
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <Box sx={{ width: "100%", mr: 5, mt: 2 }}>
                      <SingleSelect
                        dangerouslyOnChange={(e) => {
                          setSingleOffer(e);
                          const offer = offers.find(({id}) => id === e )
                          if (offer) {
                            setOfferDesc(offer.productDescription);
                            setOfferName(offer.name);
                            getPayment(e, paymentAmount.amount);
                            let stateArrayOptions = [];
                            if (!!JSON.parse(SharedApp.states)) {
                              const sts = JSON.parse(SharedApp.states).split(',');
                              setStates(JSON.stringify(sts));
                              stateArrayOptions = sts
                                .map((element) => [
                                  ...stateArrayOptions,
                                  { name: element, value: element },
                                ])
                                .flat();
                              setStatesItems(stateArrayOptions);
                              if (offer.states.length === 1) {
                                setSingleState(offer.states[0]);
                              }
                            } else {
                              setStates(JSON.stringify(offer.states));
                              stateArrayOptions = offer.states
                                .map((element) => [
                                  ...stateArrayOptions,
                                  { name: element, value: element },
                                ])
                                .flat();
                              setStatesItems(stateArrayOptions);
                              if (offer.states.length === 1) {
                                setSingleState(offer.states[0]);
                              }
                            }

                          }
                        }}
                        name="offer"
                        label={`Offer`}
                        items={offerItems}
                        readOnly={offerItems.find(offer => offer.value === formData.offerId) !== undefined && formData.locked ? true : false}
                      />
                      {/* <Text sx={{position: "relative", top: "-15px"}} variant="small">{offerDesc}</Text> */}
                    </Box>
                    {!states && (
                      <Box sx={{ width: "30%", mr: 5, mt: 2 }}>
                       <SingleSelect
                        name="state"
                        label={"State"}
                        readOnly
                        description="Select an offer first"
                        size={20}
                        items={[]}
                      />
                      </Box>
                    )}
                    {states && (
                    <Box sx={{ width: "30%", mr: 5, mt: 2 }}>
                      <StatesInput />
                    </Box>
                  )}
                  </Grid>
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <Box sx={{ mt: 2 }}>
                      <AmountInput />
                     </Box>
                  </Grid>
                </Box>
                {payment && version === "2.0" &&
                <LumosCard
                    header={{
                      title: (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <img src={getCategory(SharedApp.category)} alt={getCategoryName(SharedApp.category)} style={styles.catLogo} />
                          {getCategoryName(SharedApp.category)} estimated loan details
                        </Box>
                      ),
                    }}
                  >
                <Box
                sx={{
                    pt: 3,
                    mt: 3,
                    borderTop: "1px solid",
                    borderTopColor: "neutral.l3",
                  }}>
                  <Grid columns={[1, 1, '1fr 2fr 2fr']}>
                    <Box
                      sx={{
                        borderRight: '1px solid',
                        borderColor: ['transparent', 'transparent', 'neutral.l3'],
                        mb: 3,
                      }}
                    >
                      <div>Loan amount</div>
                      <Text variant="h3" as="h2">
                        {numberToMoney(paymentAmount.amount)}
                      </Text>
                    </Box>
                    <Box
                      sx={{
                        borderRight: '1px solid',
                        borderColor: ['transparent', 'transparent', 'neutral.l3'],
                        mb: 3,
                        pr: 2,
                      }}
                    >
                      <DataList columns={1} items={productItems} />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                      <DataList columns={1} items={estimatedPayment} />
                    </Box>
                  </Grid>
                </Box>
                </LumosCard>}
                <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    pt: 3,
                    my: 3,
                    borderTop: "1px solid",
                    borderTopColor: "neutral.l3",
                  }}>
                   <ReCAPTCHA
                    ref={captchaRef}
                    sitekey="6Ld6Kb8hAAAAADN-z4PFwBU-mBk8HcGaWkKtmSs-"
                    onChange={handleCaptchaChange}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    my: 3,
                  }}
                >
                  {spinner && (
                    <>
                      <Button type="submit" sx={{ mt: 3, px: 5 }} disabled>
                        APPLY NOW
                      </Button>
                      <Spinner
                        title="loading"
                        size="32"
                        sx={{ mt: 3, px: 3, color: GLP_BRAND.maroon }}
                      />
                    </>
                  )}
                  {!spinner && canApply && (
                    <Button
                      type="submit"
                      sx={{
                        mt: 3,
                        px: 5,
                        backgroundColor: GLP_BRAND.maroon,
                        color: GLP_BRAND.white,
                        border: "none",
                        "&:hover": { backgroundColor: GLP_BRAND.maroonHover },
                      }}
                    >
                      APPLY NOW
                    </Button>
                  )}
                  {!spinner && !canApply && (
                    <Button type="submit" sx={{ mt: 3, px: 5 }} disabled>
                      APPLY NOW
                    </Button>
                  )}
                </Box>
              </Box>
            </Card>
          </Form>
          )}
        </Box>
      )}
    </Box>
  );
};

const styles = {
  heading: {
    backgroundColor: GLP_BRAND.maroon,
  },
  loader: {
    height: "5rem"
  },
  period: {
     display: 'inline', fontSize: '70%', fontWeight: 'normal'
  },
  catLogo: {
    width: 24,
    position: "relative",
    top: 0,
    margin: "0 10px",
  }
};

export default Home;
